import { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab'
import SearchIcon from '@mui/icons-material/Search'
import TimelineIcon from '@mui/icons-material/Timeline'
import { Context } from '../../../context/AuthContext'
import PageHeader from '../../Shared/PageHeader'
import SelectSubCompany from '../../Shared/SelectSubCompany'
import LoadingBackdrop from '../../Shared/LoadingBackdrop'
import { useFeedback } from '../../Shared/FeedbackSnackbar'
import { getProductItensRfid, getRfidItemTimeline } from '../../../services/productService'
import { getProductionOrderId } from '../../../services/productionOrderServices'
import { mensagemDeErro } from '../../../services/apiClient'
import { getRfidSituation } from '../../../const/productionStatus'
import { formatDateTime } from '../../../utils/format'

const EMPTY = '—'

const formatOrEmpty = value => (value ? formatDateTime(value) : null)

const DefinitionCard = ({ label, children }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </CardContent>
  </Card>
)

const DefinitionText = ({ value }) => (
  <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
    {value || EMPTY}
  </Typography>
)

const RfidTracking = () => {
  const { subCompany } = useContext(Context)
  const navigate = useNavigate()
  const { notify, FeedbackSnackbar } = useFeedback()

  const [subCompanyId, setSubCompanyId] = useState(
    subCompany.length === 1 ? subCompany[0].subCompanyId : ''
  )
  const [epc, setEpc] = useState('')
  const [item, setItem] = useState(null)
  const [orderNumber, setOrderNumber] = useState(null)
  const [timeline, setTimeline] = useState([])
  const [searchedEpc, setSearchedEpc] = useState('')
  const [notFound, setNotFound] = useState(false)
  const [loading, setLoading] = useState(false)

  const situation = item ? getRfidSituation(item.situation) : null

  const handleSearch = async event => {
    if (event) event.preventDefault()
    if (!subCompanyId) {
      notify('Selecione uma filial para rastrear.', 'error')
      return
    }
    if (!epc.trim()) {
      notify('Informe o EPC / código RFID.', 'error')
      return
    }

    setLoading(true)
    setNotFound(false)
    setItem(null)
    setOrderNumber(null)
    setTimeline([])
    setSearchedEpc(epc.trim())

    try {
      const response = await getProductItensRfid(subCompanyId, epc.trim())
      // A rota devolve LISTA: a unicidade é (filial, EPC), então o mesmo EPC
      // pode existir em mais de uma filial. Com `subCompanyId` no filtro sobra
      // no máximo uma etiqueta.
      const [found] = Array.isArray(response.data) ? response.data : [response.data]

      if (!found) {
        setNotFound(true)
        return
      }

      setItem(found)
      const history = await getRfidItemTimeline(epc.trim())
      setTimeline(history.data.result || [])

      // `orderId` é UUID; o número legível só existe na ordem. Falha aqui não
      // derruba o rastreamento — a etiqueta já está na tela.
      if (found.orderId) {
        try {
          const ordem = await getProductionOrderId(found.orderId)
          setOrderNumber(ordem.data?.numberOtherSystem || ordem.data?.numberBihands || null)
        } catch {
          setOrderNumber(null)
        }
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setNotFound(true)
        return
      }
      notify(mensagemDeErro(error, 'Não foi possível rastrear o RFID informado.'), 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <PageHeader
        title="Rastreamento RFID"
        subtitle="Localize uma etiqueta e veja sua situação atual"
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }} component="form" onSubmit={handleSearch}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <SelectSubCompany
              value={subCompanyId}
              onChange={event => setSubCompanyId(event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              label="EPC / Código RFID"
              value={epc}
              onChange={event => setEpc(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchIcon />}
              disabled={loading}
              fullWidth
              sx={{ height: 56 }}
            >
              Rastrear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {notFound && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Nenhum RFID encontrado para o código informado.
        </Alert>
      )}

      {item && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="EPC">
                <DefinitionText value={item.epc} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Produto">
                <DefinitionText value={item.productName} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Ordem">
                <DefinitionText value={orderNumber} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Data de criação">
                <DefinitionText value={formatOrEmpty(item.createdAt)} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Última impressão">
                <DefinitionText value={formatOrEmpty(item.lastPrintedAt)} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Última leitura">
                <DefinitionText value={formatOrEmpty(item.lastReadAt)} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Local atual">
                <DefinitionText value={item.currentLocation} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              {/* O contrato não traz operador: quem leu a etiqueta é evento da
                  linha do tempo, não campo do item. */}
              <DefinitionCard label="Lote">
                <DefinitionText value={item.lot} />
              </DefinitionCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DefinitionCard label="Situação">
                <Chip
                  size="small"
                  label={item.situationLabel || situation.label}
                  color={situation.color}
                />
              </DefinitionCard>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              Histórico completo
            </Typography>
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(`/RfidTimeline/${encodeURIComponent(searchedEpc)}`)}
            >
              Ver linha do tempo completa
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ p: 2 }}>
            {timeline.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                Nenhum evento registrado para esta etiqueta.
              </Typography>
            ) : (
              <Timeline sx={{ m: 0, p: 0, '& .MuiTimelineItem-root:before': { flex: 0, p: 0 } }}>
                {timeline.map((step, index) => (
                  <TimelineItem key={step.id}>
                    <TimelineSeparator>
                      <TimelineDot color={index === timeline.length - 1 ? 'success' : 'primary'} />
                      {index < timeline.length - 1 && <TimelineConnector />}
                    </TimelineSeparator>
                    <TimelineContent sx={{ pb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {step.stageName || `Etapa ${step.order + 1}`}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Resultado: {step.qualityStatusLabel || EMPTY} · Lote: {step.lot || EMPTY}
                      </Typography>
                      {/* Etiqueta por etapa importa porque ela troca no meio da
                          produção — e é justamente a troca que fazia o histórico
                          sumir quando a busca era só pelo item do EPC. */}
                      <Typography variant="body2" color="text.secondary">
                        Etiqueta: {step.serialCurrent || EMPTY}
                        {step.tagSwapped ? ` (substituiu ${step.serialPrevious})` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateTime(step.startedAt)}
                      </Typography>
                    </TimelineContent>
                  </TimelineItem>
                ))}
              </Timeline>
            )}
          </Paper>
        </>
      )}

      <LoadingBackdrop open={loading} message="Rastreando etiqueta..." />
      <FeedbackSnackbar />
    </Box>
  )
}

export default RfidTracking
